import React, {useContext, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {CustomerTypeContext, isCustomerCompany} from "../../context/customer-type-context";
import DiscountPercentageRibbonFactory from "../../utils/DiscountPercentageRibbonFactory";
import CalculationTypeLabel from "../../components/VehicleOfferListItem/CalculationTypeLabel";
import Carousel from "react-gallery-carousel";

const SWIPE_THRESHOLD = 40;

function VehicleImage({offer}) {
    const customerContext = useContext(CustomerTypeContext);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isFirstRender, setIsFirstRender] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const thumbsRef = useRef(null);
    const touchStartX = useRef(null);
    const discountClass = isFirstRender ? "ribbon-hot-text ribbon-hot-text-active" : "ribbon-hot-text";

    let discountBadge = null;
    if (offer.discountVisible) {
        DiscountPercentageRibbonFactory.setOffer(offer);
        DiscountPercentageRibbonFactory.setIsCompany(isCustomerCompany(customerContext));
        DiscountPercentageRibbonFactory.setStyleClassName(discountClass);
        discountBadge = DiscountPercentageRibbonFactory.create();
    } else if (offer.specialOffer) {
        discountBadge = <div className="ribbon-hot-text"><p>OFERTA SPECJALNA</p></div>;
    } else if (offer.specialOfferText) {
        discountBadge = <div className="ribbon-hot-text"><p>{offer.specialOfferText}</p></div>;
    }

    const inStock = offer.availability === "IN_STOCK" || offer.availability.includes("IN_STOCK");

    useEffect(() => {
        const timers = [
            setTimeout(() => setIsFirstRender(true),  800),
            setTimeout(() => setIsFirstRender(false), 1100),
            setTimeout(() => setIsFirstRender(true),  1400),
            setTimeout(() => setIsFirstRender(false), 1700),
            setTimeout(() => setIsFirstRender(true),  2000),
            setTimeout(() => setIsFirstRender(false), 2300),
        ];
        return () => timers.forEach(clearTimeout);
    }, []);

    const images = [
        {src: offer.imageUrl},
        ...offer.additionalImageUrls.map(url => ({src: url})),
    ];

    let imageComponent = <div className="single-offer-image-container"><img className="cover-img" src={offer.imageUrl} alt=""/></div>;
    if(images.length > 1){
        imageComponent = <Carousel
            style={{height: "95%", backgroundColor: "transparent"}}
            images={images}
            canAutoPlay={false}
            hasIndexBoard={false}
            hasSizeButton={false}
            shouldMaximizeOnClick={true}
            shouldMinimizeOnClick={true}
        />;
    }

    const prev = () => setActiveIndex(i => (i - 1 + images.length) % images.length);
    const next = () => setActiveIndex(i => (i + 1) % images.length);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const deltaX = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (deltaX > SWIPE_THRESHOLD) prev();
        else if (deltaX < -SWIPE_THRESHOLD) next();
    };

    useEffect(() => {
        const activeThumb = thumbsRef.current && thumbsRef.current.children[activeIndex];
        if (activeThumb) {
            activeThumb.scrollIntoView({behavior: "smooth", inline: "center", block: "nearest"});
        }
    }, [activeIndex]);

    useEffect(() => {
        if (!isLightboxOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const escapeHandler = (e) => {
            if (e.key === "Escape") setIsLightboxOpen(false);
        };
        window.addEventListener("keydown", escapeHandler);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener("keydown", escapeHandler);
        };
    }, [isLightboxOpen]);

    const lightbox = isLightboxOpen && ReactDOM.createPortal(
        <div className="vehicle-gallery__lightbox" onClick={() => setIsLightboxOpen(false)}>
            <button
                type="button"
                className="vehicle-gallery__lightbox-close"
                onClick={() => setIsLightboxOpen(false)}
                aria-label="Zamknij podgląd"
            >
                <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round">
                    <line x1="2" y1="2" x2="18" y2="18"/>
                    <line x1="18" y1="2" x2="2" y2="18"/>
                </svg>
            </button>

            <div
                className="vehicle-gallery__lightbox-stage"
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <img src={images[activeIndex].src} alt="" className="vehicle-gallery__lightbox-img"/>

                {images.length > 1 && (
                    <>
                        <button type="button" className="vehicle-gallery__arrow vehicle-gallery__arrow--prev vehicle-gallery__lightbox-arrow" onClick={prev}>
                            <svg viewBox="0 0 8 14" width="8" height="14" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="7,1 1,7 7,13"/>
                            </svg>
                        </button>
                        <button type="button" className="vehicle-gallery__arrow vehicle-gallery__arrow--next vehicle-gallery__lightbox-arrow" onClick={next}>
                            <svg viewBox="0 0 8 14" width="8" height="14" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1,1 7,7 1,13"/>
                            </svg>
                        </button>
                    </>
                )}
            </div>
        </div>,
        document.body
    );

    return (
        <div className="vehicle-gallery">

            {/* badges row above main photo */}
            <div className="vehicle-gallery__badges">
                <CalculationTypeLabel calculationType={offer.calculationType} viewType="offer-view"/>
                {discountBadge}
                {inStock && <span className="ribbon-od-reki-grid">OD RĘKI</span>}
            </div>

            {/* main photo */}
            <div
                className="vehicle-gallery__main"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <img
                    src={images[activeIndex].src}
                    alt=""
                    className="vehicle-gallery__img"
                    onClick={() => setIsLightboxOpen(true)}
                />

                {images.length > 1 && (
                    <>
                        <button type="button" className="vehicle-gallery__arrow vehicle-gallery__arrow--prev" onClick={prev}>
                            <svg viewBox="0 0 8 14" width="8" height="14" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="7,1 1,7 7,13"/>
                            </svg>
                        </button>
                        <button type="button" className="vehicle-gallery__arrow vehicle-gallery__arrow--next" onClick={next}>
                            <svg viewBox="0 0 8 14" width="8" height="14" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1,1 7,7 1,13"/>
                            </svg>
                        </button>
                    </>
                )}
            </div>

            {/* thumbnails strip */}
            {images.length > 1 && (
                <div className="vehicle-gallery__thumbs-wrap">
                    <div className="vehicle-gallery__thumbs" ref={thumbsRef}>
                        {images.map((img, i) => (
                            <button
                                key={i}
                                type="button"
                                className={"vehicle-gallery__thumb" + (i === activeIndex ? " vehicle-gallery__thumb--active" : "")}
                                onClick={() => setActiveIndex(i)}
                            >
                                <img src={img.src} alt=""/>
                            </button>
                        ))}
                    </div>
                    <div className="vehicle-gallery__fade"/>
                </div>
            )}

            {/* info bar */}
            <div className="vehicle-gallery__info">
                {offer.isPreviewPhoto
                    ? <span className="vehicle-gallery__preview">Zdjęcia poglądowe</span>
                    : <span/>
                }
                <span className="vehicle-gallery__id">
                    ID oferty: {offer.vehisGroupId && <>{offer.vehisGroupId}/</>}{offer.id}
                </span>
            </div>

            {lightbox}

        </div>
    );
}

export default VehicleImage;
